import { HttpClient, HttpParams } from "@angular/common/http";
import { Injectable } from "@angular/core";
import { RecipeFilter } from "@app/ration-details/store/reducers/recipe.reducer";
import { Directory, QueryParams } from "@app/shared/models";
import { rationAdviceResponse } from "@app/shared/models/ration";
import { SettingsByDiseases } from "@app/shared/models/ration/diseases-settings.mode";
import { rationAutoCorrectByPortionsData } from "@app/shared/models/ration/ration-auto-correct.model";
import {
  rationDayResponse,
  rationDayResponseData,
} from "@app/shared/models/ration/ration-day-response.model";
import { rationMonthResponse } from "@app/shared/models/ration/ration-month-response.model";
import {
  rationWeekResponse,
  rationWeekResponseData,
} from "@app/shared/models/ration/ration-week-response.model.";
import { environment } from "@env/environment";
import { Observable } from "rxjs";
import { NUTRIENT_COMPOSITION } from "../consts/nutrient-composition";

export interface WeekAutoCorrectByPortionsData {
  dayId: number;
  isFreeze: boolean;
}

export interface WeekAutoCorrectByDietarySuppleentData {
  dayId: number;
  isFreeze: boolean;
}

@Injectable({
  providedIn: "root",
})
export class RationDetailsService {
  constructor(private http: HttpClient) {}

  loadRationMonth(rationId: number): Observable<rationMonthResponse> {
    return this.http.get<any>(
      `${environment.primaryApiUrl}/ration/${rationId}/month`,
    );
  }

  loadRationWeeks(rationId: number): Observable<any> {
    return this.http.get<any>(
      `${environment.primaryApiUrl}/ration/${rationId}/month/week`,
    );
  }

  sendAdviceRequest(
    rationId: number,
    rationType: "diary" | "ration",
    dayId: number,
  ): Observable<rationAdviceResponse> {
    return this.http.get<rationAdviceResponse>(
      `${environment.primaryApiUrl}/${rationType}/${rationId}/advice/day/${dayId}`,
    );
  }

  loadRationWeek(
    rationId: number,
    weekId: number,
  ): Observable<rationWeekResponse> {
    return this.http.get<any>(
      `${environment.primaryApiUrl}/ration/${rationId}/month/week/${weekId}`,
    );
  }

  removeRationWeek(
    rationId: number,
    selectedweekId: number,
    removingweekId: number,
  ): Observable<any> {
    const payload = { select_week: +selectedweekId || null };

    return this.http.request(
      "delete",
      `${environment.primaryApiUrl}/ration/${rationId}/month/week/${removingweekId}`,
      { body: payload },
    );
  }

  loadRationDays(rationId: number, weekId: number): Observable<any> {
    return this.http.get<any>(
      `${environment.primaryApiUrl}/ration/${rationId}/month/week/${weekId}/day`,
    );
  }

  loadRationDay(
    rationId: number,
    weekId: number,
    dayId: number,
  ): Observable<rationDayResponse> {
    return this.http.get<any>(
      `${environment.primaryApiUrl}/ration/${rationId}/month/week/${weekId}/day/${dayId}`,
    );
  }

  addDayRation(rationId: number, weekId: number): Observable<any> {
    // const payload = { select_day: selectedDayId };
    return this.http.post<any>(
      `${environment.primaryApiUrl}/ration/${rationId}/month/week/${weekId}/day`,
      {},
    );
  }

  removeDayRation(
    rationId: number,
    weekId: number,
    selectedDayId: number,
    removingDayId: number,
  ): Observable<any> {
    const payload = { select_day: +selectedDayId || null };

    return this.http.request(
      "delete",
      `${environment.primaryApiUrl}/ration/${rationId}/month/week/${weekId}/day/${removingDayId}`,
      { body: payload },
    );
  }

  refreshNormsDay(
    rationId: number,
    weekId: number,
    dayId: number,
  ): Observable<any> {
    return this.http.get<any>(
      `${environment.primaryApiUrl}/ration/${rationId}/month/week/${weekId}/day/${dayId}/refresh-norms`,
    );
  }

  refreshNormsWeek(rationId: number, weekId: number): Observable<any> {
    return this.http.get<any>(
      `${environment.primaryApiUrl}/ration/${rationId}/month/week/${weekId}/refresh-norms`,
    );
  }

  refreshNormsMonth(rationId: number): Observable<any> {
    return this.http.get<any>(
      `${environment.primaryApiUrl}/ration/${rationId}/month/refresh-norms`,
    );
  }

  addEatingRation(
    rationId: number,
    weekId: number,
    dayId: number,
  ): Observable<any> {
    return this.http.post<any>(
      `${environment.primaryApiUrl}/ration/${rationId}/month/week/${weekId}/day/${dayId}/eating`,
      {},
    );
  }

  updateEatingRation(
    selectedEatingCard: any,
    rationId: number,
    weekId: number,
    dayId: number,
  ): Observable<any> {
    return this.http.put<any>(
      `${environment.primaryApiUrl}/ration/${rationId}/month/week/${weekId}/day/${dayId}/eating`,
      selectedEatingCard,
    );
  }

  removeEatingRation(
    selectedEatingCard: any,
    rationId: number,
    weekId: number,
    dayId: number,
  ): Observable<any> {
    return this.http.delete<any>(
      `${environment.primaryApiUrl}/ration/${rationId}/month/week/${weekId}/day/${dayId}/eating/${selectedEatingCard.id}`,
    );
  }

  addFoodToEatingRation(
    selectedFoodCard: any,
    rationId: number,
    weekId: number,
    dayId: number,
    eatingId: number,
  ): Observable<any> {
    const payload = {
      ration_eating_id: +eatingId,
      product_id: selectedFoodCard.product_id,
      type: selectedFoodCard.type,
    };

    return this.http.post<any>(
      `${environment.primaryApiUrl}/ration/${rationId}/month/week/${weekId}/day/${dayId}/food`,
      payload,
    );
  }

  updateFoodRation(
    selectedFoodCard: any,
    rationId: number,
    weekId: number,
    dayId: number,
  ): Observable<any> {
    const payload = {
      id: selectedFoodCard.id,
      product_id: selectedFoodCard.product_id,
      type: selectedFoodCard.type,
      mass: selectedFoodCard.mass,
    };

    return this.http.put<any>(
      `${environment.primaryApiUrl}/ration/${rationId}/month/week/${weekId}/day/${dayId}/food`,
      payload,
    );
  }

  removeFoodRation(
    selectedFoodCard: any,
    rationId: number,
    weekId: number,
    dayId: number,
  ): Observable<any> {
    return this.http.delete<any>(
      `${environment.primaryApiUrl}/ration/${rationId}/month/week/${weekId}/day/${dayId}/food/${selectedFoodCard.id}`,
    );
  }

  replaceFoodRation(
    selectedReplaceableFoodCard: any,
    rationId: number,
    weekId: number,
    dayId: number,
    eatingFoodId: any,
  ): Observable<any> {
    const payload = {
      id: +eatingFoodId,
      product_id: selectedReplaceableFoodCard.product_id,
      type: selectedReplaceableFoodCard.type,
      mass: selectedReplaceableFoodCard.mass,
    };

    return this.http.put<any>(
      `${environment.primaryApiUrl}/ration/${rationId}/month/week/${weekId}/day/${dayId}/food`,
      payload,
    );
  }

  loadProducts(
    rationId: number,
    productFilter: any,
    general = 0,
  ): Observable<any> {
    const { page, ...filter } = productFilter;

    return this.http.get<any>(
      `${environment.primaryApiUrl}/ration/${rationId}/product`,
      {
        params: {
          page,
          filter: JSON.stringify(filter),
          general,
        },
      },
    );
  }

  loadRecipes(
    rationId: number,
    recipeFilter: RecipeFilter,
    general = NUTRIENT_COMPOSITION.ALL,
  ): Observable<any> {
    const { page, ...filter } = recipeFilter;

    return this.http.get<any>(
      `${environment.primaryApiUrl}/ration/${rationId}/recipe`,
      {
        params: {
          page,
          filter: JSON.stringify(filter),
          general,
        },
      },
    );
  }

  loadDietarySupplement(
    rationId: number,
    recipeFilter: any,
    general = NUTRIENT_COMPOSITION.ALL,
  ): Observable<any> {
    const { page, ...filter } = recipeFilter;

    return this.http.get<any>(
      `${environment.primaryApiUrl}/ration/${rationId}/dietary_supplement`,
      {
        params: {
          page,
          filter: JSON.stringify(filter),
          general,
        },
      },
    );
  }

  loadFoodRecommendations(
    rationId: number,
    eatingFoodId: any,
  ): Observable<any> {
    return this.http.get<any>(
      `${environment.primaryApiUrl}/ration/${rationId}/recommendation/${eatingFoodId}`,
    );
  }

  loadWeeksForCopyDayRation(rationId: number): Observable<any> {
    return this.http.get<any>(
      `${environment.primaryApiUrl}/ration/${rationId}/copy/day`,
    );
  }

  copyDayRation(
    copyweekId: number,
    rationId: number,
    weekId: number,
    dayId: number,
  ): Observable<any> {
    const payload = {
      week_id: +weekId,
      copy_week_id: +copyweekId,
      day_id: +dayId,
    };

    return this.http.post<any>(
      `${environment.primaryApiUrl}/ration/${rationId}/copy/day`,
      payload,
    );
  }

  loadWeeksForCopyEatingRation(rationId: number): Observable<any> {
    return this.http.get<any>(
      `${environment.primaryApiUrl}/ration/${rationId}/copy/eating`,
    );
  }

  copyEatingRation(
    copyDayId: number,
    rationId: number,
    dayId: number,
    eatingId: number,
  ): Observable<any> {
    const payload = {
      eating_id: +eatingId,
      day_id: +dayId,
      copy_day_id: +copyDayId,
    };

    return this.http.post<any>(
      `${environment.primaryApiUrl}/ration/${rationId}/copy/eating`,
      payload,
    );
  }

  loadRationAutoCreationDiseases(parameters?: QueryParams): Observable<any> {
    return this.http.get<any>(
      `${environment.primaryApiUrl}/ration/auto_creation/available/disease`,
      {
        params: { ...parameters },
      },
    );
  }

  loadSettingsBySelectedDiseases(
    items?: Directory[],
  ): Observable<SettingsByDiseases> {
    let params = new HttpParams();

    items?.forEach((item: any) => {
      params = params.append("disease_ids[]", item.id.toString());
    });

    return this.http.get<any>(
      `${environment.primaryApiUrl}/ration/auto_creation/disease/template`,
      {
        params: params,
      },
    );
  }

  sendAutoCorrectByPortions(
    rationId: number,
    weekId: number,
    dayId: number,
    data: rationAutoCorrectByPortionsData[],
  ): Observable<rationDayResponse> {
    return this.http.post<any>(
      `${environment.primaryApiUrl}/ration/${rationId}/month/week/${weekId}/day/${dayId}/auto_correct`,
      data,
    );
  }

  rollbackAutoCorrectByDay(
    rationId: number,
    weekId: number,
    dayId: number,
  ): Observable<{ message: string; data: rationDayResponseData }> {
    return this.http.post<any>(
      `${environment.primaryApiUrl}/ration/${rationId}/month/week/${weekId}/day/${dayId}/auto_correct/rollback`,
      {},
    );
  }

  rollbackAutoCorrectByWeek(
    rationId: number,
    weekId: number,
  ): Observable<{ message: string; data: rationWeekResponseData }> {
    return this.http.post<any>(
      `${environment.primaryApiUrl}/ration/${rationId}/month/week/${weekId}/auto_correct/rollback`,
      {},
    );
  }

  sendWeekAutoCorrectByPortions(
    rationId: number,
    weekId: number,
    data: WeekAutoCorrectByPortionsData[],
  ): Observable<rationWeekResponse> {
    return this.http.post<any>(
      `${environment.primaryApiUrl}/ration/${rationId}/month/week/${weekId}/auto_correct`,
      data,
    );
  }

  sendAutoCorrectRequestByDietarySupplement(
    rationId: number,
    weekId: number,
    data: WeekAutoCorrectByDietarySuppleentData[],
  ): Observable<rationWeekResponse> {
    return this.http.post<any>(
      `${environment.primaryApiUrl}/ration/${rationId}/month/week/${weekId}/auto_correct_dietary_suplement`,
      data,
    );
  }
}
